import { useEffect, useReducer, useCallback } from "react";

import { gql } from "apollo-boost";
import ApolloClient from "../../services/apollo";

const QUERY = gql`
  query getCars(
    $date: Date!
    $page: Int
    $limit: Int
    $departure_location_id: Int
    $arrival_location_id: Int
    $hour: String
    $minutes: String
  ) {
    getCarsVacancies(
      date: $date
      page: $page
      limit: $limit
      departure_location_id: $departure_location_id
      arrival_location_id: $arrival_location_id
      minutes: $minutes
      hour: $hour
    ) {
      car_id
      quantities
      price
      car {
        board
        number
        organization {
          name
          image
        }
      }
    }
  }
`;

interface Variables {
  date: string;
  page: number;
  limit: number;
  departure_location_id: number | string;
  arrival_location_id: number | string;
  hour: number | string;
  minutes: number | string;
}

interface useCarSearchInterface {
  loading: boolean;
  hasMore: boolean;
  error: boolean;
  list: Array<any>;
}

interface Action {
  type: "loading" | "list" | "error" | "hasMore";
  value?: any;
}

const initialState = {
  loading: true,
  hasMore: true,
  error: false,
  list: [],
} as useCarSearchInterface;

const reducer = (state: useCarSearchInterface, action: Action) => {
  switch (action.type) {
    case "list":
      return {
        ...state,
        list: action.value,
      };
    case "error":
      return {
        ...state,
        error: !state.error,
      };
    case "loading":
      return {
        ...state,
        loading: !state.loading,
      };
    case "hasMore":
      return {
        ...state,
        hasMore: action.value,
      };
    default:
      return state;
  }
};

const useCarSearch = ({
  date,
  page,
  limit,
  departure_location_id,
  arrival_location_id,
  minutes,
  hour,
}: Variables) => {
  const [{ error, hasMore, loading, list }, dispatch] = useReducer(
    reducer,
    initialState
  );

  const fetch = useCallback(() => {
    dispatch({ type: "loading" });
    if (!error) {
      return ApolloClient.query({
        query: QUERY,
        variables: {
          date,
          limit,
          page,
          departure_location_id,
          arrival_location_id,
          minutes,
          hour,
        },
        fetchPolicy: "no-cache",
      })
        .then(({ data }) => {
          dispatch({ type: "loading" });
          const listVacancies = data.getCarsVacancies;
          if (listVacancies.length !== limit) {
            dispatch({ type: "hasMore", value: false });
          }
          if (limit && page) {
            return dispatch({
              type: "list",
              value: [...list, ...listVacancies],
            });
          }
          return dispatch({ type: "list", value: listVacancies });
        })
        .catch((err) => {
          dispatch({ type: "error" });
          dispatch({ type: "hasMore", value: false });
          dispatch({ type: "loading" });
        });
    }
    return new Promise((resolve, reject) => {
      resolve();
    });
  }, [date, page, departure_location_id, arrival_location_id, minutes, hour]);

  useEffect(() => {
    fetch();
  }, [date, page, departure_location_id, arrival_location_id, minutes, hour]);

  return {
    fetch,
    list,
    loading,
    hasMore,
    error,
  };
};

export default useCarSearch;
